import createSelector from 'lib/createSelector'
import { ItineraryItemTypes, ItineraryItem } from 'components/Booking/BookingSidebar/BookingSidebarItinerary'
import { sortBy, skip } from 'lib/arrayUtils'
import moment from 'moment'
import { getAllAmounts, getSubtotal } from 'selectors/cart/cartAmountSelectors'
import { cartHasFlights } from './cart/cartSelectors'
import { OFFER_TYPE_TOUR } from 'constants/offer'

export const bookingHasFlights = createSelector(
  (state: App.State) => state.geo.currentRegionHasFlights,
  (state: App.State) => state.router.location.search,
  (state: App.State) => state.cart.flight.journey.departing.flights,
  (shouldShowFlights, search, flights) => {
    const withFlights = new URLSearchParams(search).get('withFlights') === 'true'

    return shouldShowFlights && (withFlights || flights.length > 0)
  },
)

export const getGrandTotal = createSelector(
  (state: App.State) => getSubtotal(state),
  (state: App.State) => cartHasFlights(state),
  (state: App.State) => state.cart.flight.journey.cost || state.cart.flight.basePriceSnapshot,
  // The subtotal will have the flight price when the journey is fully populated
  (subTotal, cartIncludesFlights, flightPrice) => cartIncludesFlights ? subTotal : (subTotal + flightPrice),
)

export const getTotalWithFee = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => getAllAmounts(state),
  (grandTotal, amounts) => grandTotal + amounts.propertyFeesAmount,
)

function getFlightItems(
  flights: Array<App.Flight>,
  journey: App.Journey,
): Array<ItineraryItem> {
  const flightItems: Array<ItineraryItem> = []
  if (flights.length > 0) {
    const flight = flights[0]
    const departureItem = {
      id: `${ItineraryItemTypes.FlightDeparture}${flight.flightNumber}`,
      type: ItineraryItemTypes.FlightDeparture,
      when: moment(`${flight.departingDate}T${flight.departingTime}`, moment.HTML5_FMT.DATETIME_LOCAL),
      location: flight.departingDisplayNames.primary,
      image: journey.validatingCarrierLogo && {
        url: journey.validatingCarrierLogo,
        alt: `Logo of ${journey.validatingCarrierName}`,
      },
    }
    flightItems.push(departureItem)

    const arrivalFlight = flights[flights.length - 1]
    const arrivalItem = {
      id: `${ItineraryItemTypes.FlightArrival}${arrivalFlight.flightNumber}`,
      type: ItineraryItemTypes.FlightArrival,
      when: moment(`${arrivalFlight.arrivalDate}T${arrivalFlight.arrivalTime}`, moment.HTML5_FMT.DATETIME_LOCAL),
      location: arrivalFlight.arrivalDisplayNames.primary,
    }
    flightItems.push(arrivalItem)

    // check for stop overs
    if (flights.length > 1) {
      const stopOverItems = skip(flights, 1).map(stopOver => ({
        id: `${ItineraryItemTypes.StopOver}${stopOver.flightNumber}`,
        type: ItineraryItemTypes.StopOver,
        when: moment(`${stopOver.departingDate}T${stopOver.departingTime}`, moment.HTML5_FMT.DATETIME_LOCAL),
        location: stopOver.departingDisplayNames.primary,
      }))
      flightItems.push(...stopOverItems)
    }
  }

  return flightItems
}

function getAccommodationItems(
  offerType: App.OfferParentType,
  startDate: string,
  endDate: string,
): Array<ItineraryItem> {
  const AccommodationItems: Array<ItineraryItem> = []
  if (offerType === OFFER_TYPE_TOUR) {
    AccommodationItems.push({
      id: `${ItineraryItemTypes.TourStart}`,
      type: ItineraryItemTypes.TourStart,
      when: moment(startDate).endOf('day'),
    }, {
      id: `${ItineraryItemTypes.TourEnd}`,
      type: ItineraryItemTypes.TourEnd,
      when: moment(endDate).startOf('day'),
    })
    return AccommodationItems
  }

  AccommodationItems.push({
    id: `${ItineraryItemTypes.HotelCheckIn}`,
    type: ItineraryItemTypes.HotelCheckIn,
    when: moment(startDate).endOf('day'),
  }, {
    id: `${ItineraryItemTypes.HotelCheckOut}`,
    type: ItineraryItemTypes.HotelCheckOut,
    when: moment(endDate).startOf('day'),
  })

  return AccommodationItems
}

export const getItineraryItems = createSelector(
  (state: App.State) => state.cart.accommodation.offer?.parentType ?? state.cart.bedbank.offer?.parentType,
  (state: App.State) => state.cart.accommodation.startDate ?? state.cart.bedbank.checkIn,
  (state: App.State) => state.cart.accommodation.endDate ?? state.cart.bedbank.checkOut,
  (state: App.State) => state.cart.flight.journey,
  (offerType, startDate, endDate, flightJourney) => {
    const items: Array<ItineraryItem> = []
    items.push(...getAccommodationItems(offerType, startDate, endDate))

    if (flightJourney) {
      const departingFlights = flightJourney.departing.flights
      const returningFlights = flightJourney.returning.flights
      items.push(...getFlightItems(departingFlights, flightJourney))
      items.push(...getFlightItems(returningFlights, flightJourney))
    }

    const orderedItems = sortBy(items, i => +i.when, 'asc')

    return orderedItems
  },
)

export const getExperienceItems = createSelector(
  (state: App.State) => state.cart.experiences.items,
  (state: App.State) => state.experience.experiences,
  (cartExperienceItems, experiences) => {
    return Object.values(cartExperienceItems)
      .filter(item =>
        !!experiences[item.experience.id] &&
        item.tickets.some(ticket => ticket.count > 0),
      )
  },
)

export const getTransferItems = createSelector(
  (state: App.State) => state.cart.experiences.transfers,
  (transfers) => {
    return Object.values(transfers).flatMap(transfer => [transfer?.toAirport?.transfer, transfer?.toHotel?.transfer]).filter(Boolean)
  },
)
